import _ from "lodash";
import { v4 as uuidv4 } from "uuid";
import Axios from "../apis/axios";
import { NodeId } from "components/NodeId";
import { produce } from "immer";

interface ReportPayload {
  title: string;
  lang: string;
  allsites: boolean;
  startingfrom: number;
  repetitioninterval: string;
  exportsettings: string;
  legalEntityNodeId: string;
  userNodeId: string;
  areasNodeIds: string[];
  equipmentsNodeIds: string[];
  sitesNodeIds: string[];
  nodeid: string;
  reportConfigurationNodeId: string;
  emailrecipients: string;
  sendviaemail: boolean;
}

interface ReportFlagPayload {
  isactive: boolean;
  reportConfigurationNodeId: string;
}

interface ReportProductsPayload {
  starttimestamp: number;
  endtimestamp: number;
  equipmentnodeid: string;
  legalEntityNodeId: string;
}

interface ReportCommandObject {
  parentNodeIds: string[];
  nodeId: string;
  payload: ReportPayload;
  type: "create" | "update" | "delete";
  task?: string;
}

interface ReportFlagCommandObject {
  nodeId: string;
  payload: ReportFlagPayload;
  type: "create" | "update" | "delete";
  task?: string;
}

interface ReportProductsCommandObject {
  payload: ReportProductsPayload;
  type: "create" | "update" | "delete" | "get";
  task?: string;
}

const createReportSlice = (set: (data: any) => void, get: () => any) => ({
  getReportConfigurations: {},
  getReports: {},
  executionLoader: "",
  reportConfigProducts: [],
  reportConfigProductsLoader: false,


  //Start of reportConfig
  createReportConfig: async (payload: ReportPayload) => {
    
    if (payload.equipmentsNodeIds === undefined) {
      throw new Error();
    }

    if (payload.lang === undefined) {
      payload.lang = get().lang;
    }


    if (payload.userNodeId === undefined) {
      payload.userNodeId = get().userNodeId;
    }

    const commandObjects: any[] = [];
    let parentNodeIds: string[];
    if (payload.equipmentsNodeIds) {
      parentNodeIds = payload.equipmentsNodeIds;
    } else {
      parentNodeIds = [""];
    }
    const reportCommandObject: ReportCommandObject = {
      parentNodeIds: parentNodeIds,
      nodeId: payload.nodeid ? payload.nodeid : "",
      payload: payload,
      type: "create",
      task: "processCreateReportConfigCommand",
    };
    commandObjects.push(reportCommandObject);
    
    const promises = await get().enqueueCommands(commandObjects);

    return promises;
  },

  updateReportConfig: async (payload: ReportPayload) => {
    if (payload.equipmentsNodeIds === undefined) {
      throw new Error();
    }

    if (payload.lang === undefined) {
      payload.lang = get().lang;
    }

    if (payload.userNodeId === undefined) {
      payload.userNodeId = get().userNodeId;
    }

    const commandObjects: any[] = [];
    let parentNodeIds: string[];
    if (payload.equipmentsNodeIds) {
      parentNodeIds = payload.equipmentsNodeIds;
    } else {
      parentNodeIds = [""];
    }

    const reportCommandObject: ReportCommandObject = {
      parentNodeIds: parentNodeIds,
      nodeId: payload.reportConfigurationNodeId
        ? payload.reportConfigurationNodeId
        : "",
      payload: payload,
      type: "update",
      task: "processUpdateReportConfigCommand",
    };
    commandObjects.push(reportCommandObject);

    const promises = await get().enqueueCommands(commandObjects);

    return promises;
  },

  getReportConfigProducts: async (
    starttimestamp: number,
    endtimestamp: number,
    equipmentnodeid: string,
    legalEntityNodeId: string
  ) => {
    if (legalEntityNodeId == undefined) {
      return;
    }

    const encodedReportNodeId = encodeURIComponent(legalEntityNodeId);

    try {
      set(
        produce((state: any) => {
          state.reportConfigProductsLoader = true;
        })
      );

      const response = await Axios.get(
        `/api/v1/reportconfigurations/productnames?legalentitynodeid=${encodedReportNodeId}&starttimestamp=${starttimestamp}&endtimestamp=${endtimestamp}&equipmentnodeid=${equipmentnodeid}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 10000,
        }
      );

      if (response && response?.status === 200) {
        set(
          produce((state: any) => {
            state.reportConfigProducts = response.data;
            state.reportConfigProductsLoader = false;
          })
        );
        
        return;
      } else {
        set(
          produce((state: any) => {
            state.reportConfigProductsLoader = false;
          })
        );
        return "Error";
       
      }
    } catch (error) {
      set(
        produce((state: any) => {
          state.reportConfigProductsLoader = false;
        })
      );
      return error;
      
    }
  },

  updateReportConfigFlag: async (payload: ReportFlagPayload) => {
    if (payload.isactive === undefined) {
      throw new Error();
    }

    const commandObjects: any[] = [];

    const reportCommandObject: ReportFlagCommandObject = {
      nodeId: payload.reportConfigurationNodeId
        ? payload.reportConfigurationNodeId
        : "",
      payload: payload,
      type: "update",
      task: "processUpdateReportConfigCommandFlag",
    };
    commandObjects.push(reportCommandObject);

    const promises = await get().enqueueCommands(commandObjects);

    return promises;
  },

  executeReportConfiguration: async (payload: any) => {
    if (payload.reportConfigurationNodeId === undefined) {
      throw new Error();
    }

    const commandObjects: any[] = [];

    const reportCommandObject: any = {
      nodeId: payload.reportConfigurationNodeId
        ? payload.reportConfigurationNodeId
        : "",
      payload: payload,
      type: "get",
      task: "processExecuteReportConfiguration",
    };
    commandObjects.push(reportCommandObject);

    const promises = await get().enqueueCommands(commandObjects);

    return promises;
  },

  deleteReportConfiguration: async (payload: any) => {
    const deleteCommandObject = {
      parentNodeIds: payload.equipmentsNodeIds,
      nodeId: payload.reportConfigurationNodeId,
      payload: payload,
      type: "delete",
      task: "processDeleteReportConfigCommand",
    };
    let promises = get().enqueueCommands([deleteCommandObject]);

    return promises;
  },

  getAllReportConfigurations: async (legalEntityNodeId: string) => {
    set(
      produce((state: any) => {
        state.getReportConfigurations = [];
        state.getReports = {};
        state.executionLoader = false;
      })
    );

    try {
      if (get().isLoggedIn === true) {
        if (legalEntityNodeId == undefined) {
          return;
        }
        const encodedReportNodeId = encodeURIComponent(legalEntityNodeId);
        const response = await Axios.get(
          `api/v1/reportconfigurations?legalentitynodeid=${encodedReportNodeId}`,
          {
            headers: {
              "Content-Type": "application/json",
              
            },
          }
        );

        if (response && response?.status == 200) {
          set(
            produce((state: any) => {
              state.getReportConfigurations = response.data;
              state.getReports = {};
            })
          );

          // Return the response data after updating the state
          return response.data;
        } else {
          throw new Error();
        }
      }
    } catch (error) {
      throw new Error(String(error));
    }
  },

  processCreateReportConfigCommand: async (
    commandObject: ReportCommandObject
  ) => {
    try {
      

      const response = await Axios.post(
        "api/v1/reportconfigurations",
        commandObject.payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 10000,
        }
      );

      if (response && response?.status === 200) {
        
        return response.data;
      } else {
        throw new Error();
      }
    } catch (error) {
      
      throw new Error(String(error));
    }
  },

  processUpdateReportConfigCommand: async (
    commandObject: ReportCommandObject
  ) => {
    try {
      
      const encodedReportNodeId = encodeURIComponent(
        commandObject.payload.reportConfigurationNodeId || ""
      );
      const response = await Axios.put(
        "/api/v1/reportconfigurations/" + encodedReportNodeId,
        commandObject.payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 10000,
        }
      );

      if (response && response?.status === 200) {
        
        return;
      } else {
        throw new Error();
      }
    } catch (error) {
      throw new Error(String(error));
    }
  },

  processUpdateReportConfigCommandFlag: async (
    commandObject: ReportCommandObject
  ) => {
    try {
      
      const encodedReportNodeId = encodeURIComponent(
        commandObject.payload.reportConfigurationNodeId || ""
      );
      const response = await Axios.put(
        "/api/v1/reportconfigurations/" + encodedReportNodeId,
        commandObject.payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 10000,
        }
      );

      if (response && response?.status === 200) {
       
        return;
      } else {
        throw new Error();
      }
    } catch (error) {
      throw new Error(String(error));
    }
  },

  processExecuteReportConfiguration: async (
    commandObject: ReportCommandObject
  ) => {
    try {
      set(
        produce((state: any) => {
          state.executionLoader = "loading";
        })
      );
      
      const encodedReportNodeId = encodeURIComponent(
        commandObject.payload.reportConfigurationNodeId || ""
      );
      const response = await Axios.get(
        "/api/v1/reports/manual/" + encodedReportNodeId,
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 10000,
        }
      );

      if (response && response?.status === 200) {
        set(
          produce((state: any) => {
            state.executionLoader = "loaded";
          })
        );
        
        return;
      } else {
        set(
          produce((state: any) => {
            state.executionLoader = "loaded";
          })
        );
        throw new Error();
      }
    } catch (error) {
      set(
        produce((state: any) => {
          state.executionLoader = "loaded";
        })
      );
      throw new Error(String(error));
    }
  },

  processDeleteReportConfigCommand: async (
    commandObject: ReportCommandObject
  ) => {
    try {
      

      const encodedReportNodeId = encodeURIComponent(
        commandObject.payload.reportConfigurationNodeId || ""
      );
      const response = await Axios.delete(
        "/api/v1/reportconfigurations/" + encodedReportNodeId,
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 10000,
        }
      );

      if (response && response?.status === 200) {
       
        return;
      } else {
        throw new Error();
      }
    } catch (error) {
      throw new Error(String(error));
    }
  },

  //Start of reports
  getAllReports: async (nodeId: string) => {
    try {
      if (get().isAuthUser === true) {
        if (nodeId == undefined) {
          return;
        }
        const encodedReportNodeId = encodeURIComponent(nodeId);
        const response = await Axios.get(
          `api/v1/reports?reportconfigurationnodeid=${encodedReportNodeId}`,
          {
            headers: {
              "Content-Type": "application/json",
             
            },
          }
        );

        if (response && response?.status == 200) {
          set(
            produce((state: any) => {
              state.getReports = response.data;
            })
          );
          // Return the response data after updating the state
          return response.data;
        } else {
          throw new Error();
        }
      }
    } catch (error) {
      throw new Error(String(error));
    }
  },
});

export default createReportSlice;
